/**
 * The saved vocabulary: lists (#56-#59) and tags (#60-#62).
 *
 * Both are small, global and read by three surfaces at once (the save popup's checkboxes, the
 * `/saved` sidebar, the typeahead), so they are plain cached queries under the `['saved']` root —
 * a save/unsave invalidates that root and every one of them refreshes together, counts included.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { SavedListDTO, SavedListsResponse, SavedTagDTO, SavedTagsResponse } from '@lw/shared';
import {
  createSavedList,
  createSavedTag,
  deleteSavedList,
  deleteSavedTag,
  getSavedLists,
  getSavedTags,
  renameSavedList,
  renameSavedTag,
} from '../../api/saved';
import { QK } from '../../api/queryKeys';

export function useSavedListsQuery(): UseQueryResult<SavedListsResponse, Error> {
  return useQuery({
    queryKey: QK.savedLists(),
    queryFn: ({ signal }) => getSavedLists(signal),
  });
}

export function useSavedTagsQuery(): UseQueryResult<SavedTagsResponse, Error> {
  return useQuery({
    queryKey: QK.savedTags(),
    queryFn: ({ signal }) => getSavedTags(signal),
  });
}

/**
 * Every mutation below invalidates the whole `['saved']` root rather than one key: renaming a tag
 * changes what a card's chip says, deleting a list changes a sidebar count *and* a popup checkbox,
 * and none of it is worth hand-patching three caches for.
 */
function useSavedInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: QK.saved });
}

export function useCreateSavedList() {
  const invalidate = useSavedInvalidation();
  return useMutation<SavedListDTO, Error, string>({
    mutationFn: (name) => createSavedList(name),
    onSuccess: invalidate,
  });
}

export function useRenameSavedList() {
  const invalidate = useSavedInvalidation();
  return useMutation<SavedListDTO, Error, { id: number; name: string }>({
    mutationFn: ({ id, name }) => renameSavedList(id, name),
    onSuccess: invalidate,
  });
}

export function useDeleteSavedList() {
  const invalidate = useSavedInvalidation();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteSavedList(id),
    onSuccess: invalidate,
  });
}

export function useCreateSavedTag() {
  const invalidate = useSavedInvalidation();
  return useMutation<SavedTagDTO, Error, string>({
    mutationFn: (name) => createSavedTag(name),
    onSuccess: invalidate,
  });
}

export function useRenameSavedTag() {
  const invalidate = useSavedInvalidation();
  return useMutation<SavedTagDTO, Error, { id: number; name: string }>({
    mutationFn: ({ id, name }) => renameSavedTag(id, name),
    onSuccess: invalidate,
  });
}

export function useDeleteSavedTag() {
  const invalidate = useSavedInvalidation();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteSavedTag(id),
    onSuccess: invalidate,
  });
}
